// ログインの状態とプロフィール（ランキングに出る名前）。
// 名前は Google の名前を使わない（本名をランキングに出さない）。最初のログインで「しずかなペンギン」のような仮の名前が付き、
// ログインのあとの窓（ui/name-dialog.ts）で本人が決める。同じ名前は 1 人だけ（関数の予約の表。functions/src/username-core.ts）。
import type { UserInfo } from "@appful/sdk/protocol";
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut as fbSignOut, type User } from "firebase/auth";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";
import { getLocale } from "./i18n";

export interface SessionState {
  user: User | null;
  displayName: string | null;
  /** 名前の後ろに付く4桁（マイページに出す。名前を予約していない人は、ランキングにも「名前#4桁」で出る） */
  tag: string | null;
  /** 名前を決めたか（ログインのあとの窓で「この名前にする」を押した）。プロフィールを読めなかったときは null */
  nameChosen: boolean | null;
  /** 最初の判定（前回のログインの復元）が済んだか */
  ready: boolean;
}

let state: SessionState = { user: null, displayName: null, tag: null, nameChosen: null, ready: false };
const listeners = new Set<(s: SessionState) => void>();

function emit(next: SessionState) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

export function session(): SessionState {
  return state;
}

export function onSession(fn: (s: SessionState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function userInfo(): UserInfo {
  return { signedIn: state.user !== null, displayName: state.user ? state.displayName : null };
}

export function whenReady(): Promise<SessionState> {
  if (state.ready) return Promise.resolve(state);
  return new Promise((resolve) => {
    const off = onSession((s) => {
      if (s.ready) {
        off();
        resolve(s);
      }
    });
  });
}

/**
 * ログインの画面が閉じたあと、ログインの状態が揃うまで待つ。窓が閉じても、呼び名の用意が済むまでは
 * session() にまだ出ない。ログインしていなければ（窓を閉じただけ）すぐ false を返す。
 */
export function whenSignedIn(timeoutMs = 20_000): Promise<boolean> {
  if (state.user) return Promise.resolve(true);
  if (!auth.currentUser) return Promise.resolve(false);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      off();
      resolve(false);
    }, timeoutMs);
    const off = onSession((s) => {
      if (s.user) {
        clearTimeout(timer);
        off();
        resolve(true);
      }
    });
  });
}

/**
 * まだプロフィールが無ければ、関数（rollNickname）が仮の名前を付けて作る。あれば今の名前を返す。
 * 名前を書けるのは関数だけ（本人が打った名前も、関数 claimUsername が確かめてから書く）。
 */
async function ensureProfile(user: User): Promise<{ displayName: string; tag: string; nameChosen: boolean }> {
  const snap = await getDoc(doc(db, "users", user.uid));
  const existing = snap.get("displayName");
  if (typeof existing === "string") return { displayName: existing, tag: String(snap.get("tag") ?? ""), nameChosen: snap.get("nameChosen") === true };
  const res = await httpsCallable<unknown, { displayName: string; tag: string }>(functions, "rollNickname")({ locale: getLocale() });
  return { ...res.data, nameChosen: false };
}

/**
 * 対戦のお休みの期限（まだ先なら、その時刻）。無い・過ぎた・形が変・読めないときは null。
 * 期限を書くのは管理の台本（tools/reports.mjs）だけ。部屋の Worker も同じ期限で断るので、ここは本人に理由を知らせるため。
 * 対戦を選ぶたびに読み直す（ログインのあとにお休みになることがある）。
 */
export async function lobbySuspendedUntil(): Promise<Date | null> {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  try {
    const v: unknown = (await getDoc(doc(db, "users", uid))).get("lobbySuspendedUntil");
    return v instanceof Timestamp && v.toMillis() > Date.now() ? v.toDate() : null;
  } catch (e) {
    console.warn("[auth] suspension check failed", e);
    return null;
  }
}

export function startAuth() {
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      emit({ user: null, displayName: null, tag: null, nameChosen: null, ready: true });
      return;
    }
    try {
      const p = await ensureProfile(user);
      emit({ user, displayName: p.displayName, tag: p.tag, nameChosen: p.nameChosen, ready: true });
    } catch (e) {
      console.error("[auth] profile load failed", e);
      emit({ user, displayName: "Player", tag: "", nameChosen: null, ready: true });
    }
  });
}

export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(auth, provider);
  // ログインし直したら、前に「あとで決める」を押した人にも、名前を決める窓をもう一度出す
  forgetNameLater();
}

// 名前を決める窓の「あとで決める」。押した人には、次にログインするまで窓を出さない（この端末だけ。消えても窓が 1 回出るだけ）
const NAME_LATER_KEY = "appful:name-later";

/** 「あとで決める」を押した（その人の uid を覚える） */
export function rememberNameLater(uid: string) {
  try {
    localStorage.setItem(NAME_LATER_KEY, uid);
  } catch {
    /* 覚えられない環境では、次に開いたときにまた窓が出る */
  }
}

/** その人が「あとで決める」を押したまま、まだログインし直していないか */
export function nameLater(uid: string): boolean {
  try {
    return localStorage.getItem(NAME_LATER_KEY) === uid;
  } catch {
    return false;
  }
}

function forgetNameLater() {
  try {
    localStorage.removeItem(NAME_LATER_KEY);
  } catch {
    /* 読めない環境では何もしない */
  }
}

export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

/** 名前の答え（関数 checkUsername・claimUsername。functions/src/username-core.ts） */
export type NameAnswer = "ok" | "taken" | "ng" | "length";

/** 「おまかせ」：ほかの人が使っていない自動の名前を 1 つもらう（まだ決めない） */
export async function suggestUsername(): Promise<string> {
  const res = await httpsCallable<unknown, { displayName: string }>(functions, "rollNickname")({ locale: getLocale(), suggest: true });
  return res.data.displayName;
}

/** 名前が使えるかの目安（決めるときに関数がもう一度確かめる） */
export async function checkUsername(name: string): Promise<NameAnswer> {
  const res = await httpsCallable<unknown, { result: NameAnswer }>(functions, "checkUsername")({ name });
  return res.data.result;
}

export type ClaimResult = { status: "ok"; displayName: string } | { status: Exclude<NameAnswer, "ok"> | "too_many" | "not_ready" };

/** 名前を決める。決まったら、上の帯・マイページ・ランキングの名前を変える。not_ready は「準備中」（今の名前の人を予約の表へ入れている間） */
export async function claimUsername(name: string): Promise<ClaimResult> {
  const res = await httpsCallable<unknown, ClaimResult>(functions, "claimUsername")({ name });
  if (res.data.status === "ok" && state.user) {
    emit({ ...state, displayName: res.data.displayName, nameChosen: true });
  }
  return res.data;
}

/** 本人のデータ（プロフィール・セーブ・ランキング）とアカウントを消す。消すのは関数（deleteAccount）。 */
export async function deleteAccount(): Promise<void> {
  await httpsCallable(functions, "deleteAccount")();
  await fbSignOut(auth).catch(() => undefined);
}
